import { useState, useEffect } from "react";
import { Star, X } from "lucide-react";

const GOOGLE_REVIEW_URL = "https://maps.app.goo.gl/z3aZDGowkqnnBQ9S8?g_st=ic";

/**
 * Inline, non-intrusive prompt that appears below a form after a successful
 * submission. Lets the customer optionally leave a Google review when their
 * job is complete. No popups, no modals — pure inline.
 *
 * Props:
 *   - visible: boolean — show/hide
 *   - onDismiss: () => void
 *   - testid: optional data-testid suffix
 */
export default function GoogleReviewPrompt({ visible, onDismiss, testid = "default" }) {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    if (visible) {
      // small delay for a fade-in feel
      const t = setTimeout(() => setMounted(true), 30);
      return () => clearTimeout(t);
    }
    setMounted(false);
  }, [visible]);

  if (!visible) return null;

  return (
    <div
      data-testid={`google-review-prompt-${testid}`}
      className={`mt-4 bg-white border border-black/10 rounded-sm p-4 sm:p-5 flex items-start gap-3 transition-all duration-300 ${
        mounted ? "opacity-100 translate-y-0" : "opacity-0 translate-y-1"
      }`}
    >
      {/* Google G mark */}
      <div className="w-9 h-9 bg-white border border-black/10 rounded-full flex items-center justify-center shrink-0" aria-hidden>
        <svg viewBox="0 0 24 24" className="w-5 h-5" xmlns="http://www.w3.org/2000/svg">
          <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.28 1.42-1.07 2.62-2.28 3.42v2.84h3.69c2.16-1.99 3.61-4.93 3.61-8.5z"/>
          <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.69-2.84c-1.02.69-2.32 1.09-4.24 1.09-3.26 0-6.02-2.2-7.01-5.16H1.18v3.24C3.15 21.31 7.27 24 12 24z"/>
          <path fill="#FBBC05" d="M4.99 14.18A7.2 7.2 0 0 1 4.62 12c0-.76.13-1.49.37-2.18V6.58H1.18A11.99 11.99 0 0 0 0 12c0 1.93.46 3.76 1.18 5.42l3.81-3.24z"/>
          <path fill="#EA4335" d="M12 4.84c1.77 0 3.35.61 4.6 1.81l3.27-3.27C17.95 1.5 15.24.5 12 .5 7.27.5 3.15 3.19 1.18 6.58l3.81 3.24C5.98 7.04 8.74 4.84 12 4.84z"/>
        </svg>
      </div>

      {/* Copy + CTA */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-[#0A0A0A] leading-tight">Once your move is done — mind leaving a quick Google review?</p>
        <p className="text-xs text-[#475569] mt-1 leading-relaxed">Takes 30 seconds. Helps Dublin neighbours find a reliable van.</p>
        <a
          href={GOOGLE_REVIEW_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`leave-google-review-link-${testid}`}
          className="mt-2 inline-flex items-center gap-1.5 text-[13px] font-bold text-[#0055FF] hover:underline"
        >
          <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
          Leave a Google review
        </a>
      </div>

      {/* Dismiss */}
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss review prompt"
        data-testid={`dismiss-google-review-${testid}`}
        className="shrink-0 w-7 h-7 flex items-center justify-center text-[#475569] hover:text-[#0A0A0A] hover:bg-black/5 rounded-sm transition-colors"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
